import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from "react-toastify";
import {
    faFileZipper,
    faXmark,
    faTrash
} from "@fortawesome/free-solid-svg-icons";

import useBreakpoint from '../../../hooks/useBreakpoint';
import useUser from '../../../hooks/useUser';
import { Spinner } from 'react-bootstrap';

import GET_OUTGOING_TRAINEESHIP from '../../../graphql/queries/traineeships/getOutgoingTraineeship';
import ACCEPT_TRAINEESHIP from '../../../graphql/mutations/traineeships/acceptTraineeship';
import DECLINE_TRAINEESHIP from '../../../graphql/mutations/traineeships/declineTraineeship';
import DELETE_TRAINEESHIP from '../../../graphql/mutations/traineeships/deleteTraineeship';

import CustomTab from '../../../components/CustomTab';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../../components/CustomModal';
import TraineeshipInfoTable from './components/TraineeshipInfoTable';
import StudentInfo from './components/StudentInfo';
import InstitutionInfoTable from './components/InstitutionInfoTable';
import TraineeshipProgrammeTable from './components/TraineeshipProgrammeTable';
import TraineeshipRecognitionTable from './components/TraineeshipRecognitionTable';
import TraineeshipSupportTable from './components/TraineeshipSupportTable';
import TraineeshipChangesTable from './components/TraineeshipChangesTable';
import { StatusActionPair } from '../../../data/statusActionPairs';
import COLORS from '../../../data/colors';
import toastConfiguration from '../../../data/toastConfiguration';
import CertificateTable from './components/CertificateTable';

import "react-toastify/dist/ReactToastify.css";
import './index.scss';

const SingleViewTraineeship = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'traineeships.singleView' });

    const [size] = useBreakpoint();
    const { traineeshipID } = useParams();
    const { user } = useUser();
    let location = useLocation();
    const navigate = useNavigate();
    const [showModalReject, setShowModalReject] = useState(false);
    const [showModalSign, setShowModalSign] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [rejectionMessage, setRejectionMessage] = useState("");
    const [signed, setSigned] = useState(false);

    const { loading, data, error, refetch } = useQuery(GET_OUTGOING_TRAINEESHIP, {
        variables: { id: traineeshipID }
    });

    const [
        acceptTraineeshipMutation,
        {
            loading: loadingAcceptTraineeship,
            error: errorAcceptTraineeshipMutation,
        },
    ] = useMutation(ACCEPT_TRAINEESHIP);

    const [
        declineTraineeshipMutation,
        {
            loading: loadingDeclineTraineeship,
            error: errorDeclineTraineeshipMutation,
        },
    ] = useMutation(DECLINE_TRAINEESHIP);

    const [
        deleteTraineeshipMutation,
        {
            loading: loadingDeleteTraineeship,
            error: errorDeleteTraineeshipMutation,
        },
    ] = useMutation(DELETE_TRAINEESHIP);

    useEffect(() => {
        if (data) {
            // Always check for sending institution (outgoing)
            if (user?.erasmusCode !== data?.traineeship?.sending_institution?.field_hei_erasmus_code) {
                navigate("/unauthorized");
            }
        }
    }, [data]);

    const position = "Responsible person at the Sending Institution";
    const typeOfStudent = "outgoing"; // Always outgoing for traineeships
    const institution = 1; // Always 1 for outgoing/sending institution

    const shouldDisableSignButtons = (
      signed ||
      StatusActionPair["traineeship-typeOfStudent-1-" + data?.traineeship?.field_tr_status] !== "act" ||
      user?.permissions.find((permission) => permission.value === "managingStudents") === undefined
  );

    const handleCloseModalSign = () => setShowModalSign(false);
    const handleShowModalSign = () => setShowModalSign(true);
    const handleCloseModalReject = () => setShowModalReject(false);
    const handleShowModalReject = () => setShowModalReject(true);
    const handleCloseModalDelete = () => setShowModalDelete(false);
    const handleShowModalDelete = () => setShowModalDelete(true);

    const handleSignButtonClick = async () => {
        const toastId = toast.loading(t('signFeedback.loading'), toastConfiguration);
        setSigned(true);
        try {
            let result = await acceptTraineeshipMutation({
              "variables": {
                "email": user?.email,
                "institution": institution,
                "name": user?.userName ? user?.userName : user?.email,
                "traineeshipID": data?.traineeship?.traineeshipID,
                "position": position,
                "status": data?.traineeship?.field_tr_status
              }
            });
            handleCloseModalSign();
            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: t('signFeedback.success'),
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: t('signFeedback.failure'),
                type: "error",
                isLoading: false,
            });
        }
    };

    const handleDeclineButtonClick = async () => {
        const toastId = toast.loading(t('declineFeedback.loading'), toastConfiguration);
        setSigned(true);
        try {
            let result = await declineTraineeshipMutation({
              "variables": {
                "email": user?.email,
                "institution": institution,
                "name": user?.userName ? user?.userName : user?.email,
                "traineeshipID": data?.traineeship?.traineeshipID,
                "reason": rejectionMessage,
                "position": position,
                "status": data?.traineeship?.field_tr_status
              }
            });
            handleCloseModalReject();
            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: t('declineFeedback.success'),
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: t('decline.Feedback.failure'),
                type: "error",
                isLoading: false,
            });
        }
    };

    const shouldDisableDeleteButton = () => {
        // Check if user has required permissions
        if (user?.permissions.find(permission => permission.value === "managingStudents") === undefined) {
            return true;
        }

        const traineeshipStatus = data?.traineeship?.field_tr_status;
        const preliminaryCommitments = data?.traineeship?.commitment_preliminary || [];

        // Allow deletion if:
        // 1. Status is 'preliminary_signed-by-student' OR 'Signed by student' OR 'signed_by_student' OR
        // 2. Status is 'preliminary_unsigned' OR 'unsigned' OR 'draft' AND no preliminary commitments exist
        const canDelete =
        traineeshipStatus === 'preliminary_signed-by-student' ||
        traineeshipStatus === 'Signed by student' ||
        traineeshipStatus === 'signed by student' ||
        traineeshipStatus === 'signed_by_student' ||
        ((traineeshipStatus === 'preliminary_unsigned' ||
            traineeshipStatus === 'unsigned' ||
            traineeshipStatus === 'draft') &&
            preliminaryCommitments.length === 0);

        return !canDelete;
    };

    const handleDeleteButtonClick = async () => {
        const toastId = toast.loading(t('deleteFeedback.loading'), toastConfiguration);

        try {
            let result = await deleteTraineeshipMutation({
                variables: {
                    traineeshipID: data?.traineeship?.traineeshipID,
                    pathName: "outgoing" // Always outgoing for traineeships
                }
            });

            handleCloseModalDelete();

            if (!result?.errors && result?.data?.deleteTraineeship?.success) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: t('deleteFeedback.success'),
                    type: "success",
                    isLoading: false,
                });
                navigate("/traineeships/outgoing");
            }
        } catch (error) {
            toast.update(toastId, {
                ...toastConfiguration,
                render: t('deleteFeedback.failure'),
                type: "error",
                isLoading: false,
            });
        }
    };

    const hasCertificateData = () => {
        const tableD = data?.traineeship?.table_d;
        if (!tableD) return false;
        
        // Check if table_d exists and has at least one non-empty field
        const hasData = Object.values(tableD).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
        });
        
        return hasData;
    };

    if (error)
        return (
            <div className="text-center text-danger mx-auto">{t('errorMessage')}</div>
        );

    if (loading) {
        return (
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)"
                }}
            >
                <Spinner
                    as="div"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="align-middle my-auto"
                />
                <span>{t('loadingMessage')}</span>
            </div>
        )
    }

    return (
        <>
            <Row style={size !== "md" && size !== "lg" && size !== "xl"? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 75 }}>
                    <Tabs id="application-info">
                        <CustomTab eventKey="home" title={t('traineeshipInfo.title')} style={{ background: "#ffff" }}>
                            <TraineeshipInfoTable data={data} size={size} typeOfStudent="outgoing" />
                        </CustomTab>
                    </Tabs>
                </Col>
            </Row>
            
            <Row style={size !== "md" && size !== "lg" && size !== "xl" ? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 36 }}>
                    <Tabs id="second-tab">
                        {/* General Info Tab */}
                        <CustomTab eventKey="general-info" id="general-info" title={t('generalInfo.title')} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="student-info">
                                        <CustomTab eventKey="home" title={t('generalInfo.studentPersonalData.title')} style={{ background: "#ffff" }}>
                                            <StudentInfo data={data} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="sending-hei">
                                        <CustomTab eventKey="home" title={t('generalInfo.sendingInstInfo.title')} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"sending"}
                                                size={size}
                                                data={data?.traineeship?.sending_institution}
                                                contact_person={data?.traineeship?.sending_contact_person}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="receiving-org">
                                        <CustomTab eventKey="home" title={t('generalInfo.receivingInstInfo.title')} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"receiving"}
                                                size={size}
                                                data={data?.traineeship?.receiving_organization}
                                                contact_person={data?.traineeship?.receiving_contact_person}
                                                receiving_mentor={data?.traineeship?.receiving_mentor}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Traineeship Programme Tab (Table A) */}
                        <CustomTab eventKey="programme" id="programme" title={t('traineeshipProgramme.title')} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="programme-details">
                                        <CustomTab eventKey="home" title={t("traineeshipProgramme.details.tableTitle")} style={{ background: "#ffff" }}>
                                            <TraineeshipProgrammeTable data={data?.traineeship?.table_a} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Changes to Traineeship Programme Tab (Table A2) */}
                        <CustomTab eventKey="changes" id="changes" title={t('changes.tabTitle')} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="changes-details">
                                        <CustomTab eventKey="home" title={t('changes.tableTitle')} style={{ background: "#ffff" }}>
                                            <TraineeshipChangesTable data={data?.traineeship?.final_table_a2} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Recognition & Support Tab (Tables B & C) */}
                        <CustomTab eventKey="recognition" id="recognition" title={t('recognitionAndSupport.tabTitle')} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="recognition-details">
                                        <CustomTab eventKey="home" title={t('recognitionAndSupport.tableB.title')} style={{ background: "#ffff" }}>
                                            <TraineeshipRecognitionTable
                                                data={data?.traineeship?.table_b}
                                                type={data?.traineeship?.table_b_type?.name}
                                                accidentInsurance={data?.traineeship?.table_b_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="support-details">
                                        <CustomTab eventKey="home" title={t('recognitionAndSupport.tableC.title')} style={{ background: "#ffff" }}>
                                            <TraineeshipSupportTable
                                                data={data?.traineeship?.table_c}
                                                accidentInsurance={data?.traineeship?.table_c_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Certificate Tab - Only show if certificate data exists */}
                        {hasCertificateData() && (
                            <CustomTab eventKey="certificate" id="certificate" title={t('traineeshipCertificate.tabTitle')} style={{ background: "#EAECF3" }}>
                                <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                    <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                        <Tabs defaultActiveKey="home" id="certificate-details">
                                            <CustomTab eventKey="home" title={t('traineeshipCertificate.tableTitle')} style={{ background: "#ffff" }}>
                                                <CertificateTable data={data?.traineeship?.table_d} size={size} />
                                            </CustomTab>
                                        </Tabs>
                                    </Col>
                                </Row>
                            </CustomTab>
                        )}
                    </Tabs>
                </Col>
            </Row>

            {!(size !== "md" && size !== "lg" && size !== "xl" && size !== "xxl") ?
                <Row style={{ marginLeft: 35, marginRight: 35 }}>
                    <Col style={{ marginTop: 36, display: "flex", justifyContent: "flex-end" }}>
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"decline"}
                            text={t('modalReject.declineButton')}
                            icon={faXmark}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalReject}
                        />
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"accept"}
                            text={t('modalSign.signButton')}
                            styles={{ marginLeft: 20 }}
                            icon={faFileZipper}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalSign}
                        />
                        <CustomButton
                            disabled={shouldDisableDeleteButton()}
                            buttonType={"delete"}
                            text={t('modalDelete.deleteButton')}
                            icon={faTrash}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalDelete}
                            styles={{ marginLeft: 20 }}
                        />
                    </Col>
                </Row>
                :
                <Row>
                    <Col style={{ marginTop: 15, paddingLeft: 0, paddingRight: 0 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                                buttonType={"decline"}
                                text={"Decline"}
                                icon={faXmark}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalReject}
                            />
                        </div>
                    </Col>
                    <Col style={{ marginTop: 15, paddingLeft: 5, paddingRight: 5 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                buttonType={"accept"}
                                text={"Sign"}
                                icon={faFileZipper}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalSign}
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                            />
                        </div>
                    </Col>
                    <Col style={{ marginTop: 15, paddingLeft: 0, display: "flex", justifyContent: "flex-end", paddingRight: 0 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                styles={{ width: "100%" }}
                                disabled={shouldDisableDeleteButton()}
                                buttonType={"delete"}
                                text={t('modalDelete.deleteButton')}
                                icon={faTrash}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalDelete}
                            />
                        </div>
                    </Col>
                </Row>
            }

          {/* Modals */}
          <CustomModal
              show={showModalReject}
              handleClose={handleCloseModalReject}
              fullscreen={false}
              body={
                  <div>
                      {t('modalReject.body.part1')}<br />
                      <div style={{ background: "#EAECF3", padding: "5%" }}>
                          <h6 className='h6-style'>{t('modalReject.body.part2')}</h6>
                          <textarea
                              style={{ width: "100%" }}
                              rows={3}
                              value={rejectionMessage}
                              onChange={(e) => setRejectionMessage(e.target.value)}
                          />
                          <h6 style={{ color: "#6C757D" }}>{t('modalReject.body.part3')}</h6>
                      </div>
                  </div>
              }
              button={
                  <CustomButton
                      disabled={!rejectionMessage || loadingDeclineTraineeship}
                      buttonType={"decline"}
                      text={t('modalReject.declineButton')}
                      icon={faXmark}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      handleClick={handleDeclineButtonClick}
                  />
              }
              title={
                  <div>
                      {t('modalReject.title.part1')}
                      <span style={{ color: "red" }}>{t('modalReject.title.part2')}</span>
                        {t('modalReject.title.part3')}
                  </div>
              }
          />

          <CustomModal
              show={showModalSign}
              handleClose={handleCloseModalSign}
              fullscreen={false}
              body={<div>{t('modalSign.body')}</div>}
              button={
                  <CustomButton
                      disabled={loadingAcceptTraineeship}
                      buttonType={"accept"}
                      text={t('modalSign.signButton')}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      icon={faFileZipper}
                      handleClick={handleSignButtonClick}
                  />
              }
              title={
                  <div style={{ paddingBottom: "20px" }}>
                      {t('modalSign.title.part1')}
                      <span style={{ color: "green" }}>{t('modalSign.title.part2')}</span>
                      {t('modalSign.title.part3')}
                  </div>
              }
          />

          <CustomModal
              show={showModalDelete}
              handleClose={handleCloseModalDelete}
              fullscreen={false}
              body={
                  <div>
                      {t('modalDelete.body')}
                  </div>
              }
              button={
                  <CustomButton
                      disabled={loadingDeleteTraineeship}
                      buttonType={"delete"}
                      text={t('modalDelete.confirmButton')}
                      icon={faTrash}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      handleClick={handleDeleteButtonClick}
                  />
              }
              title={
                  <div>
                      {t('modalDelete.title.part1')}
                      <span style={{ color: "red" }}>{t('modalDelete.title.part2')}</span>
                      {t('modalDelete.title.part3')}
                  </div>
              }
          />

          <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
          />
      </>
    );

};

export default SingleViewTraineeship;